import { useEffect, useRef } from 'react';
import type React from 'react';

import { useIsDarkMode } from '#src/app/shared/hooks/dom';
import { useDLocal } from '#src/app/shared/hooks/payments';
import { isDarkModeApplied } from '#src/app/shared/utils/dom';
import type { Field, FieldType, SmartFieldEvent } from '#src/app/shared/utils/payments';

type Props = {
	type: FieldType;
	onBlur?: (event: SmartFieldEvent) => void;
	onFocus?: (event: SmartFieldEvent) => void;
	onFieldCreated?: (field: Field) => void;
	placeholder?: string;
};

export const DLocalSmartField: React.FC<Props> = ({ type, onBlur, onFocus, onFieldCreated, placeholder }) => {
	const { smartFields, status } = useDLocal();
	// tracked to re-run this effect on theme changes; the actual value read at creation time comes
	// from isDarkModeApplied() below, since this state can still be stale the instant status flips
	// to 'ready' (e.g. once the dLocal script is warm in the browser cache).
	const isDarkMode = useIsDarkMode();
	const fieldPlaceholder = useRef<HTMLDivElement>(null);
	const fieldRef = useRef<Field>(undefined);

	useEffect(() => {
		if (status !== 'ready' || smartFields === null || fieldPlaceholder.current === null || !!fieldRef.current) {
			return;
		}
		const isDark = isDarkModeApplied();
		const field = smartFields.create(type, {
			style: {
				base: {
					fontSize: 16,
					lineHeight: 18,
					backgroundColor: isDark ? '#1c1e23' : '#ffffff',
					color: isDark ? '#ffffff' : '#2d2f37',
					'::placeholder': {
						color: isDark ? '#cbcfda' : 'rgba(58, 62, 73, 0.8)',
					},
				},
			},
			placeholder,
			hideIcon: true,
		});
		if (onFieldCreated) {
			onFieldCreated(field);
		}
		field.mount(fieldPlaceholder.current);
		if (onBlur) {
			field.on('blur', onBlur);
		}
		if (onFocus) {
			field.on('focus', onFocus);
		}
		fieldRef.current = field;
	}, [status, smartFields, fieldPlaceholder, fieldRef, type, onBlur, onFocus, onFieldCreated, placeholder, isDarkMode]);

	return <div ref={fieldPlaceholder}></div>;
};
